import React, {useEffect, useRef, useState} from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    Divider
} from '@mui/material';
import PropTypes from 'prop-types';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Checkbox from '@mui/material/Checkbox';
import {useGetGroupAccessQuery, useManageGroupAccessMutation} from '../api/services/groupService';
import {useGetActiveUsersQuery} from '../api/services/accountService';
import {ROLES, ROLES_LABELS} from '../constants/roles';
import useNotification from '../utility/hooks/useNotification';

const ManageAccessDialog = ({open, onClose, nodeId, groupName}) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedRoleNames, setSelectedRoleNames] = useState([]);
    const usersPrefilledRef = useRef(false);
    const rolesPrefilledRef = useRef(false);
    const [manageGroupAccess, {isLoading: isManaging}] = useManageGroupAccessMutation();
    const {showSuccessNotification, showErrorNotification} = useNotification();

    const {data: availableUsers = [], isLoading: isLoadingUsers} = useGetActiveUsersQuery({}, {
        skip: !open
    });

    const filteredRoleNames = [
        ROLES.SCHOOL_ADMIN,
        ROLES.MEMBER_OF_STAFF,
        ROLES.SENIOR_MEMBER_OF_STAFF
    ];

    const {data: groupAccess = {}, isLoading: isLoadingGroupAccess} = useGetGroupAccessQuery(nodeId, {
        skip: !open || !nodeId
    });

    const accessEntry = Array.isArray(groupAccess)
        ? (groupAccess[0] || {})
        : (groupAccess || {});
    const serverUserIds = Array.isArray(accessEntry.userIds)
        ? accessEntry.userIds
        : (Array.isArray(accessEntry.users) ? accessEntry.users : []);

    const serverRoleNames = Array.isArray(accessEntry.roles)
        ? accessEntry.roles
        : [];

    useEffect(() => {
        if (open) {
            setSelectedUsers([]);
            setSelectedRoleNames([]);
            usersPrefilledRef.current = false;
            rolesPrefilledRef.current = false;
        }
    }, [open, nodeId]);

    useEffect(() => {
        if (
            open &&
            !usersPrefilledRef.current &&
            selectedUsers.length === 0 &&
            availableUsers.length > 0 &&
            serverUserIds.length > 0
        ) {
            const currentUsers = serverUserIds
                .map(userId => availableUsers.find(user => user.id === userId))
                .filter(Boolean);
            setSelectedUsers(currentUsers);
            usersPrefilledRef.current = true;
        }
    }, [open, availableUsers, serverUserIds, selectedUsers.length]);

    useEffect(() => {
        if (
            open &&
            !rolesPrefilledRef.current &&
            selectedRoleNames.length === 0 &&
            filteredRoleNames.length > 0 &&
            serverRoleNames.length > 0
        ) {
            const allowedLower = filteredRoleNames.map(n => String(n).toLowerCase());
            const currentRoleNames = serverRoleNames
                .filter(roleName => allowedLower.includes(String(roleName).toLowerCase()));
            setSelectedRoleNames(currentRoleNames);
            rolesPrefilledRef.current = true;
        }
    }, [open, filteredRoleNames, serverRoleNames, selectedRoleNames.length]);

    const handleSave = async () => {
        if (selectedUsers.length === 0 && selectedRoleNames.length === 0) {
            showErrorNotification('At least one user or role must be selected');
            return;
        }

        try {
            const userIds = selectedUsers.map(user => user.id);

            await manageGroupAccess({
                groupFilterId: nodeId,
                userIds,
                roles: selectedRoleNames
            }).unwrap();

            showSuccessNotification('Group access updated successfully');
            onClose();
        } catch (error) {
            showErrorNotification('Failed to update group access');
        }
    };

    const handleClose = () => {
        setSelectedUsers([]);
        setSelectedRoleNames([]);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                Manage Access - {groupName}
            </DialogTitle>
            <DialogContent>
                <Box sx={{mt: 2}}>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                        Select users and/or roles that should have access to this group.
                    </Typography>

                    <Box sx={{mb: 3}}>
                        <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 'bold'}}>
                            Users
                        </Typography>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            limitTags={5}
                            options={availableUsers}
                            getOptionLabel={(option) => option.name || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedUsers}
                            onChange={(event, newValue) => {
                                if (newValue.length === 0 && selectedUsers.length > 0 && selectedRoleNames.length === 0) {
                                    showErrorNotification('At least one user or role must remain in the group');
                                    return;
                                }
                                setSelectedUsers(newValue);
                            }}
                            loading={isLoadingUsers || isLoadingGroupAccess}
                            disabled={isManaging}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Users"
                                    placeholder="Start typing a name..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {(isLoadingUsers || isLoadingGroupAccess) ? <CircularProgress size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option, {selected}) => (
                                <li {...props} key={option.id}>
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                        checked={selected}
                                    />
                                    {option.name}
                                </li>
                            )}
                        />
                    </Box>

                    <Divider sx={{my: 2}} />

                    <Box>
                        <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 'bold'}}>
                            Roles
                        </Typography>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            limitTags={5}
                            options={filteredRoleNames}
                            getOptionLabel={(name) => ROLES_LABELS[name] || name || ''}
                            isOptionEqualToValue={(option, value) => String(option || '').toLowerCase() === String(value || '').toLowerCase()}
                            value={selectedRoleNames}
                            onChange={(event, newValue) => {
                                if (newValue.length === 0 && selectedRoleNames.length > 0 && selectedUsers.length === 0) {
                                    showErrorNotification('At least one user or role must remain in the group');
                                    return;
                                }
                                setSelectedRoleNames(newValue);
                            }}
                            loading={isLoadingGroupAccess}
                            disabled={isManaging}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Roles"
                                    placeholder="Select roles..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoadingGroupAccess ? <CircularProgress size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, name, {selected}) => (
                                <li {...props} key={name}>
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                        checked={selected}
                                    />
                                    {ROLES_LABELS[name] || name}
                                </li>
                            )}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isManaging}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={isManaging || (selectedUsers.length === 0 && selectedRoleNames.length === 0)}
                    startIcon={isManaging ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {isManaging ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ManageAccessDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    nodeId: PropTypes.number,
    groupName: PropTypes.string
};

export default ManageAccessDialog;
